import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useReportFilters } from '~/context/ReportFiltersProvider';
import { FaEye, FaSort, FaSortUp, FaSortDown, FaSync, FaTimes, FaDownload, FaCog } from 'react-icons/fa';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { Pagination } from '~/components/Pagination';
import { searchCampaignRequests } from '~/services/campaign';
import { ALL_AVAILABLE_COLUMNS, DEFAULT_VISIBLE_COLUMNS, parseVisibleColumns } from './campaignRequestColumns';

interface SearchResult {
    id: number;
    patient_first_name: string;
    patient_last_name: string;
    patient_email: string;
    patient_phone1: string;
    initial_first_name: string;
    initial_last_name: string;
    initial_email: string;
    initial_phone: string;
    status: {
        id: number;
        name: string;
        code: string;
    };
    created_date: string;
    medical_reason: {
        id: number;
        code: string;
        name: string;
    } | null;
    primary_medical_issue: {
        id: number;
        code: string;
        name: string;
    } | null;
    patient_services_coordinator: {
        id: number;
        full_name: string;
    } | null;
    prescreen_result?: {
        id: number;
        name: string;
    } | null;
    contact_attempts_count?: number;
}

const CampaignRequestSearchResults: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [results, setResults] = useState<SearchResult[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [sortField, setSortField] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [refreshKey, setRefreshKey] = useState(0);

    // Column visibility state
    const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(
        () => parseVisibleColumns(searchParams.get('visible_columns')),
    );
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const columnSelectorRef = useRef<HTMLDivElement>(null);

    // Close column selector when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target as Node)) {
                setShowColumnSelector(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Get visible columns in order
    const columns = useMemo(() => {
        return ALL_AVAILABLE_COLUMNS.filter(col => visibleColumnKeys.includes(col.key));
    }, [visibleColumnKeys]);

    const handleColumnToggle = (columnKey: string) => {
        setVisibleColumnKeys(prev => {
            if (prev.includes(columnKey)) {
                // Don't allow removing all columns
                if (prev.length === 1) return prev;
                return prev.filter(k => k !== columnKey);
            } else {
                return [...prev, columnKey];
            }
        });
    };

    const handleResetColumns = () => {
        setVisibleColumnKeys(DEFAULT_VISIBLE_COLUMNS);
    };

    const handleRefresh = useCallback(() => {
        setRefreshKey(prev => prev + 1);
    }, []);

    // Function to render cell content based on column type
    const renderCellContent = (request: SearchResult, columnKey: string) => {
        switch (columnKey) {
            case 'patient_name':
                return `${request.patient_first_name || ''} ${request.patient_last_name || ''}`.trim();
            case 'patient_email':
                return request.patient_email || '';
            case 'patient_phone':
                return request.patient_phone1 || '';
            case 'initial_contact_name':
                return `${request.initial_first_name || ''} ${request.initial_last_name || ''}`.trim();
            case 'initial_contact_email':
                return request.initial_email || '';
            case 'initial_contact_phone':
                return request.initial_phone || '';
            case 'status':
                return request.status?.name || '';
            case 'medical_reason':
                return request.medical_reason?.name || '';
            case 'primary_medical_issue':
                return request.primary_medical_issue?.name || '';
            case 'coordinator':
                return request.patient_services_coordinator?.full_name || '';
            case 'created_date':
                return request.created_date
                    ? new Date(request.created_date).toLocaleDateString()
                    : '';
            case 'prescreen_result':
                return request.prescreen_result?.name || '';
            case 'contact_attempts':
                // Stringify rather than returning a number: every other branch
                // returns a string, and the CSV export feeds this straight into
                // escapeCSV. 0 is meaningful (nobody has called yet), so it has
                // to render as "0" rather than fall through to ''.
                return String(request.contact_attempts_count ?? 0);
            default:
                return '';
        }
    };

    useEffect(() => {
        const performSearch = async () => {
            setLoading(true);
            setError(null);

            try {
                // Get page from URL params or default to 1
                const page = parseInt(searchParams.get('page') || '1', 10);
                setCurrentPage(page);

                // Get sorting from URL params
                const sortBy = searchParams.get('sortBy');
                const sort = searchParams.get('sort');
                if (sortBy) {
                    setSortField(sortBy);
                    setSortDirection(sort === 'asc' ? 'asc' : 'desc');
                }

                // Convert URLSearchParams to filters object
                const filters: Record<string, any> = {};
                searchParams.forEach((value, key) => {
                    if (key !== 'page' && key !== 'visible_columns') {
                        if (filters[key]) {
                            // Handle multiple values for the same key (for multi-select fields)
                            if (Array.isArray(filters[key])) {
                                filters[key].push(value);
                            } else {
                                filters[key] = [filters[key], value];
                            }
                        } else {
                            filters[key] = value;
                        }
                    }
                });

                const response = await searchCampaignRequests(filters, page);
                setResults(response.results || response);

                // Handle pagination data from Django REST framework
                if (response.count !== undefined) {
                    setTotalCount(response.count);
                    // Calculate total pages (20 items per page as configured in backend)
                    setTotalPages(Math.ceil(response.count / 20));
                } else {
                    // Fallback: if no pagination data, assume we have all results
                    setTotalCount(Array.isArray(response.results) ? response.results.length : (Array.isArray(response) ? response.length : 0));
                    setTotalPages(1);
                }
            } catch (err) {
                setError('Failed to perform search. Please try again.');
                console.error('Search error:', err);
            } finally {
                setLoading(false);
            }
        };

        performSearch();
    }, [searchParams, refreshKey]);

    const handleBackToSearch = () => {
        // Preserve search parameters when going back (excluding page and sort)
        const params = new URLSearchParams(searchParams);
        params.delete('page');
        params.delete('sortBy');
        params.delete('sort');
        navigate(`/new-report?${params.toString()}`);
    };

    const handlePageChange = (newPage: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('page', newPage.toString());
        setSearchParams(params);
    };

    const handleSort = (field: string) => {
        const params = new URLSearchParams(searchParams);

        // If clicking the same field, toggle direction
        if (sortField === field) {
            const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
            setSortDirection(newDirection);
            params.set('sortBy', field);
            params.set('sort', newDirection);
        } else {
            // New field, default to ascending
            setSortField(field);
            setSortDirection('asc');
            params.set('sortBy', field);
            params.set('sort', 'asc');
        }

        // Reset to first page when sorting
        params.set('page', '1');
        setSearchParams(params);
    };

    const getSortIcon = (field: string) => {
        if (sortField !== field) {
            return <FaSort className="ml-1 text-gray-400" />;
        }
        return sortDirection === 'asc' ?
            <FaSortUp className="ml-1 text-primary" /> :
            <FaSortDown className="ml-1 text-primary" />;
    };

    // Get active filters for display (exclude pagination and sorting params)
    const activeFilters = useMemo(() => {
        const filters: { key: string; label: string; value: string }[] = [];
        const excludeKeys = ['page', 'sortBy', 'sort', 'visible_columns'];
        
        const labelMap: Record<string, string> = {
            name: 'Name',
            status: 'Status',
            start_date: 'Created From',
            end_date: 'Created To',
            approve_start_date: 'Approved From',
            approve_end_date: 'Approved To',
            pend_financial_need_start_date: 'Pend Financial Need From',
            pend_financial_need_end_date: 'Pend Financial Need To',
            reject_start_date: 'Rejected From',
            reject_end_date: 'Rejected To',
        };

        searchParams.forEach((value, key) => {
            if (!excludeKeys.includes(key) && value) {
                filters.push({
                    key,
                    label: labelMap[key] || key,
                    value,
                });
            }
        });

        return filters;
    }, [searchParams]);

    // Remove a specific filter
    const handleRemoveFilter = (key: string, value?: string) => {
        const params = new URLSearchParams(searchParams);
        
        // For multi-value params (like status), remove only the specific value
        const allValues = params.getAll(key);
        if (allValues.length > 1 && value) {
            params.delete(key);
            allValues.filter(v => v !== value).forEach(v => params.append(key, v));
        } else {
            params.delete(key);
        }
        
        // Reset to page 1 when filters change
        params.set('page', '1');
        setSearchParams(params);
    };

    // Clear all filters
    const { clearFilterParams } = useReportFilters();
    const handleClearAllFilters = () => {
        clearFilterParams();
        navigate('/new-report');
    };

    const [isDownloading, setIsDownloading] = useState(false);

    // Download results as CSV (fetches ALL pages)
    const handleDownloadCSV = useCallback(async () => {
        if (totalCount === 0) return;

        setIsDownloading(true);

        try {
            // Build filters object from search params (excluding pagination/sort for fetching all)
            const filters: Record<string, any> = {};
            const excludeKeys = ['page', 'sortBy', 'sort', 'visible_columns'];
            searchParams.forEach((value, key) => {
                if (!excludeKeys.includes(key)) {
                    if (filters[key]) {
                        if (Array.isArray(filters[key])) {
                            filters[key].push(value);
                        } else {
                            filters[key] = [filters[key], value];
                        }
                    } else {
                        filters[key] = value;
                    }
                }
            });

            // Fetch all pages of results
            const allResults: SearchResult[] = [];
            const pageSize = 20; // Backend page size
            const totalPagesToFetch = Math.ceil(totalCount / pageSize);

            for (let page = 1; page <= totalPagesToFetch; page++) {
                const response = await searchCampaignRequests(filters, page);
                const pageResults = response.results || response;
                if (Array.isArray(pageResults)) {
                    allResults.push(...pageResults);
                }
            }

            if (allResults.length === 0) {
                console.warn('No results to download');
                return;
            }

            // Use only the user-selected visible columns for CSV export
            const csvColumns = columns.map(col => ({
                key: col.key,
                label: col.label,
            }));

            // Helper to escape CSV values
            const escapeCSV = (value: string | null | undefined): string => {
                if (value === null || value === undefined) return '';
                const str = String(value);
                // Escape quotes and wrap in quotes if contains comma, quote, or newline
                if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            };

            // Build CSV content
            const header = csvColumns.map(col => escapeCSV(col.label)).join(',');

            const rows = allResults.map((request: SearchResult) => {
                return csvColumns.map(col => {
                    // Reuse the renderCellContent logic for consistency
                    return escapeCSV(renderCellContent(request, col.key));
                }).join(',');
            });

            const csvContent = [header, ...rows].join('\n');

            // Create and trigger download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `campaign-requests-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading CSV:', err);
        } finally {
            setIsDownloading(false);
        }
    }, [totalCount, searchParams, columns]);

    if (loading) {
        return (
            <>
                <Breadcrumb pageName='Campaign Request Report' parentPage='Reports' parentPath='/new-report' />
                <div className='flex justify-center items-center h-64'>
                    <div className='text-lg'>Loading search results...</div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Breadcrumb pageName='Campaign Request Report' parentPage='Reports' parentPath='/new-report' />
                <div className='rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default sm:px-7.5 xl:pb-1'>
                    <div className='text-center py-8'>
                        <div className='text-red-500 text-lg mb-4'>{error}</div>
                        <button
                            onClick={handleBackToSearch}
                            className='rounded-lg bg-primary px-6 py-2 text-white hover:bg-primary/90 font-medium'
                        >
                            Back to Search
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Breadcrumb pageName='Campaign Request Report' parentPage='Reports' parentPath='/new-report' />

            <div className='rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default sm:px-7.5 xl:pb-1'>
                <div className='flex justify-between items-center mb-6'>
                    <p className='text-sm text-gray-600'>
                        {totalCount > 0 ? (
                            totalPages > 1 ?
                                `Showing ${((currentPage - 1) * 20) + 1}-${Math.min(currentPage * 20, totalCount)} of ${totalCount} results` :
                                `${totalCount} result${totalCount !== 1 ? 's' : ''} found`
                        ) : 'No results found'}
                    </p>
                    <div className='flex gap-3'>
                        {/* Column Selector */}
                        <div className='relative' ref={columnSelectorRef}>
                            <button
                                onClick={() => setShowColumnSelector(!showColumnSelector)}
                                className='inline-flex items-center rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-300 transition-colors duration-200'
                                title='Configure visible columns'
                            >
                                <FaCog className='mr-2' size={14} />
                                Columns
                            </button>
                            {showColumnSelector && (
                                <div className='absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50'>
                                    <div className='p-3 border-b border-gray-200'>
                                        <div className='flex justify-between items-center'>
                                            <span className='font-medium text-sm'>Visible Columns</span>
                                            <button
                                                onClick={handleResetColumns}
                                                className='text-xs text-primary hover:underline'
                                            >
                                                Reset to default
                                            </button>
                                        </div>
                                    </div>
                                    <div className='p-2 max-h-80 overflow-y-auto'>
                                        {ALL_AVAILABLE_COLUMNS.map(col => (
                                            <label
                                                key={col.key}
                                                className='flex items-center px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer'
                                            >
                                                <input
                                                    type='checkbox'
                                                    checked={visibleColumnKeys.includes(col.key)}
                                                    onChange={() => handleColumnToggle(col.key)}
                                                    className='w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary'
                                                />
                                                <span className='ml-2 text-sm text-gray-700'>{col.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={handleDownloadCSV}
                            disabled={loading || isDownloading || totalCount === 0}
                            className='inline-flex items-center rounded-lg bg-green-100 px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-200 focus:outline-none focus:ring-4 focus:ring-green-300 disabled:opacity-50 transition-colors duration-200'
                            title={totalCount > 20 ? `Download all ${totalCount} results as CSV` : 'Download results as CSV'}
                        >
                            {isDownloading ? (
                                <FaSync className='mr-2 animate-spin' size={14} />
                            ) : (
                                <FaDownload className='mr-2' size={14} />
                            )}
                            {isDownloading ? 'Downloading...' : `Download CSV${totalCount > 20 ? ` (${totalCount})` : ''}`}
                        </button>
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className='inline-flex items-center rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-300 disabled:opacity-50 transition-colors duration-200'
                            title='Refresh results'
                        >
                            <FaSync className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={14} />
                            Refresh
                        </button>
                        <button
                            onClick={handleBackToSearch}
                            className='rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/50 transition-colors duration-200'
                        >
                            Back to Search
                        </button>
                    </div>
                </div>

                {/* Active Filters Display */}
                {activeFilters.length > 0 && (
                    <div className='mb-6 flex flex-wrap items-center gap-2'>
                        <span className='text-sm font-medium text-gray-600'>Active filters:</span>
                        {activeFilters.map((filter, index) => (
                            <span
                                key={`${filter.key}-${filter.value}-${index}`}
                                className='inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary'
                            >
                                <span className='text-gray-500'>{filter.label}:</span>
                                <span>{filter.value}</span>
                                <button
                                    onClick={() => handleRemoveFilter(filter.key, filter.value)}
                                    className='ml-1 rounded-full p-0.5 hover:bg-primary/20 transition-colors'
                                    title={`Remove ${filter.label} filter`}
                                >
                                    <FaTimes size={10} />
                                </button>
                            </span>
                        ))}
                        <button
                            onClick={handleClearAllFilters}
                            className='ml-2 text-sm text-gray-500 hover:text-red-500 underline transition-colors'
                        >
                            Clear all
                        </button>
                    </div>
                )}

                {results.length === 0 && !loading ? (
                    <div className='text-center py-8'>
                        <div className='text-lg text-gray-600 mb-4'>No campaign requests found matching your criteria.</div>
                        <button
                            onClick={handleBackToSearch}
                            className='rounded-lg bg-primary px-6 py-2 text-white hover:bg-primary/90 font-medium'
                        >
                            Try Different Filters
                        </button>
                    </div>
                ) : results.length > 0 && (
                    <div className='max-w-full overflow-x-auto'>
                        <table className='w-full table-auto'>
                            <thead>
                                <tr className='bg-gray-2 text-left'>
                                    {columns.map((column, index) => (
                                        <th
                                            key={column.key}
                                            className={`min-w-[120px] py-4 px-4 font-medium text-black cursor-pointer hover:bg-gray-3 select-none ${index === 0 ? 'xl:pl-11' : ''
                                                }`}
                                            onClick={() => handleSort(column.sortKey)}
                                        >
                                            <div className='flex items-center'>
                                                {column.label}
                                                {getSortIcon(column.sortKey)}
                                            </div>
                                        </th>
                                    ))}
                                    <th className='py-4 px-4 font-medium text-black'>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((request) => (
                                    <tr key={request.id}>
                                        {columns.map((column, index) => (
                                            <td
                                                key={column.key}
                                                className={`border-b border-[#eee] py-5 px-4 ${index === 0 ? 'pl-9 xl:pl-11' : ''
                                                    }`}
                                            >
                                                <p className='text-black'>
                                                    {renderCellContent(request, column.key)}
                                                </p>
                                            </td>
                                        ))}
                                        <td className='border-b border-[#eee] py-5 px-4'>
                                            <button
                                                onClick={() => navigate(`/campaign-requests/${request.id}`)}
                                                className='flex items-center justify-center w-8 h-8 rounded text-primary hover:text-primary/80 hover:bg-primary/10 transition-colors duration-200'
                                                title='View Campaign Request Details'
                                            >
                                                <FaEye size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination - only show if there are results and more than one page */}
                {results.length > 0 && totalPages > 1 && (
                    <Pagination
                        page={currentPage}
                        setPage={handlePageChange}
                        pages={totalPages}
                        totalCount={totalCount}
                    />
                )}
            </div>
        </>
    );
};

export default CampaignRequestSearchResults;

